import React, { useState } from 'react';
import { Button, Card, Typography, Alert, Spin, Space, Divider } from 'antd';
import { DatabaseOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import mongoRealService from '../../services/mongoRealService';

const { Title, Text } = Typography;

const MongoTest = () => {
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState({});

  const testMongoConnection = async () => {
    setTesting(true);
    const testResults = {};

    try {
      // Test 1: Connection
      testResults.connection = { success: false, message: 'Testing connection...' };
      setResults({ ...testResults });

      await mongoRealService.connect();
      testResults.connection = { success: true, message: 'Successfully connected to MongoDB Atlas' };
      setResults({ ...testResults });

      // Test 2: Seed data
      testResults.seedData = { success: false, message: 'Seeding sample data...' };
      setResults({ ...testResults });

      await mongoRealService.seedInitialData();
      testResults.seedData = { success: true, message: 'Sample data seeded successfully' };
      setResults({ ...testResults });

      // Test 3: Authentication
      testResults.auth = { success: false, message: 'Testing authentication...' };
      setResults({ ...testResults });

      const authResult = await mongoRealService.authenticateEmployee('admin@company.com', 'admin123');
      testResults.auth = { 
        success: true, 
        message: `Authentication successful for ${authResult.name}`,
        data: authResult
      };
      setResults({ ...testResults });

      // Test 4: Fetch projects
      testResults.projects = { success: false, message: 'Fetching projects...' };
      setResults({ ...testResults });

      const projects = await mongoRealService.getProjects();
      testResults.projects = { 
        success: true, 
        message: `Found ${projects.length} projects`,
        data: projects
      };
      setResults({ ...testResults });

      // Test 5: Fetch timesheets
      testResults.timesheets = { success: false, message: 'Fetching timesheets...' };
      setResults({ ...testResults });

      const timesheets = await mongoRealService.getAllTimesheets();
      testResults.timesheets = { 
        success: true, 
        message: `Found ${timesheets.length} timesheets`,
        data: timesheets
      };
      setResults({ ...testResults });

    } catch (error) {
      const failedTest = Object.keys(testResults).find(key => !testResults[key].success);
      if (failedTest) {
        testResults[failedTest] = { 
          success: false, 
          error: error.message 
        };
      } else {
        testResults.general = { 
          success: false, 
          error: error.message 
        };
      }
      setResults({ ...testResults });
    }

    setTesting(false);
  };

  const TestResult = ({ title, result }) => {
    if (!result) return null;

    return (
      <div className="mb-4">
        <div className="flex items-center mb-2">
          {result.success ? (
            <CheckCircleOutlined className="text-green-500 mr-2" />
          ) : (
            <CloseCircleOutlined className="text-red-500 mr-2" />
          )}
          <Text strong>{title}</Text>
        </div>
        {result.success ? (
          <Alert
            message={result.message}
            type="success"
            showIcon={false}
            className="mb-2"
          />
        ) : (
          <Alert
            message="Test Failed"
            description={result.error || result.message}
            type="error"
            showIcon={false}
            className="mb-2"
          />
        )}
        {result.data && (
          <details className="mt-2">
            <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
              View Data ({Array.isArray(result.data) ? result.data.length : 1} items)
            </summary>
            <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-40">
              {JSON.stringify(result.data, null, 2)}
            </pre>
          </details>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <Card className="shadow-lg">
        <div className="text-center mb-6">
          <Title level={3}>
            <DatabaseOutlined className="mr-2" />
            MongoDB Atlas Connection Test
          </Title>
          <Text className="text-gray-600">
            Test connection to MongoDB Atlas and verify data operations
          </Text>
        </div>

        <div className="mb-6">
          <Alert
            message="Configuration"
            description={
              <div>
                <p><strong>Database:</strong> {process.env.REACT_APP_DATABASE_NAME || 'timesheet_db'}</p>
                <p><strong>Connection:</strong> MongoDB Atlas</p>
                <p><strong>Test Credentials:</strong> admin@company.com / admin123</p>
              </div>
            }
            type="info"
            showIcon
            className="mb-4"
          />
        </div>

        <div className="text-center mb-6">
          <Button
            type="primary"
            size="large"
            icon={<DatabaseOutlined />}
            onClick={testMongoConnection}
            loading={testing}
          >
            {testing ? 'Testing MongoDB...' : 'Test MongoDB Connection'}
          </Button>
        </div>

        {testing && (
          <div className="text-center mb-6">
            <Spin size="large" />
            <p className="mt-2 text-gray-600">Testing MongoDB Atlas connection...</p>
          </div>
        )}

        {Object.keys(results).length > 0 && (
          <div>
            <Title level={4}>Test Results</Title>
            <TestResult title="Database Connection" result={results.connection} />
            <TestResult title="Seed Sample Data" result={results.seedData} />
            <TestResult title="Employee Authentication" result={results.auth} />
            <TestResult title="Fetch Projects" result={results.projects} />
            <TestResult title="Fetch Timesheets" result={results.timesheets} />
            {results.general && <TestResult title="General Error" result={results.general} />}
          </div>
        )}

        <Divider />

        <div className="mt-6 p-4 bg-green-50 rounded-lg">
          <Title level={5}>What this test does:</Title>
          <Space direction="vertical" className="w-full">
            <Text className="text-sm">
              <strong>1. Connect:</strong> Establishes connection to MongoDB Atlas
            </Text>
            <Text className="text-sm">
              <strong>2. Seed Data:</strong> Creates sample employees, projects, and timesheets
            </Text>
            <Text className="text-sm">
              <strong>3. Authenticate:</strong> Tests login with john@company.com
            </Text>
            <Text className="text-sm">
              <strong>4. Fetch Data:</strong> Retrieves projects and timesheets from database
            </Text>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default MongoTest;
